/**
 * Czysta logika Ogrodu marzeń (WP9) — unlock, cena, kwiatki.
 * PLAN §7.4, §11 (R33).
 */
import { GARDEN_PRICE_ZL } from '@pieskowo/shared';

export const LOCAL_GARDEN_UNLOCKED_KEY = 'pieskowo.gardenUnlocked';
export const LOCAL_GARDEN_JSON_KEY = 'pieskowo.gardenJson';
export const LOCAL_BALANCE_KEY = 'pieskowo.balanceZl';

export { GARDEN_PRICE_ZL };

export type FlowerKind = 'rose' | 'tulip' | 'daisy' | 'sunflower';

export type PlantedFlower = {
  kind: FlowerKind;
  x: number;
  y: number;
};

export const FLOWER_CATALOG: {
  kind: FlowerKind;
  /** Legacy (statusy); wizualnie = proceduralna tekstura `texture`. */
  emoji: string;
  namePl: string;
  color: number;
  texture: string;
}[] = [
  {
    kind: 'rose',
    emoji: '🌹',
    namePl: 'Róża',
    color: 0xf48fb1,
    texture: 'obj_garden_flower_rose',
  },
  {
    kind: 'tulip',
    emoji: '🌷',
    namePl: 'Tulipan',
    color: 0xef9a9a,
    texture: 'obj_garden_flower_tulip',
  },
  {
    kind: 'daisy',
    emoji: '🌼',
    namePl: 'Stokrotka',
    color: 0xfff59d,
    texture: 'obj_garden_flower_daisy',
  },
  {
    kind: 'sunflower',
    emoji: '🌻',
    namePl: 'Słonecznik',
    color: 0xffd54f,
    texture: 'obj_garden_flower_sunflower',
  },
];

/** Klucz tekstury proceduralnego kwiatka (kolor per gatunek w rysunku). */
export function flowerTextureKey(kind: FlowerKind): string {
  return (
    FLOWER_CATALOG.find((f) => f.kind === kind)?.texture ??
    'obj_garden_flower_daisy'
  );
}

export function flowerColor(kind: FlowerKind): number {
  return FLOWER_CATALOG.find((f) => f.kind === kind)?.color ?? 0xfff59d;
}

export function canAffordGarden(balanceZl: number, price = GARDEN_PRICE_ZL): boolean {
  return Number.isInteger(balanceZl) && balanceZl >= price;
}

export type UnlockResult =
  | { ok: true; balanceAfter: number }
  | { ok: false; reason: 'already' | 'funds' };

/** Offline unlock (gdy serwer niedostępny) — bez kary, tylko komunikat. */
export function unlockGardenLocal(
  unlocked: boolean,
  balanceZl: number,
  price = GARDEN_PRICE_ZL,
): UnlockResult {
  if (unlocked) return { ok: false, reason: 'already' };
  if (!canAffordGarden(balanceZl, price)) return { ok: false, reason: 'funds' };
  return { ok: true, balanceAfter: balanceZl - price };
}

export function normalizeGardenJson(raw: unknown): PlantedFlower[] {
  if (!Array.isArray(raw)) return [];
  const kinds = new Set(FLOWER_CATALOG.map((f) => f.kind));
  const out: PlantedFlower[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    if (typeof o.kind !== 'string' || !kinds.has(o.kind as FlowerKind)) continue;
    if (typeof o.x !== 'number' || typeof o.y !== 'number') continue;
    if (!Number.isFinite(o.x) || !Number.isFinite(o.y)) continue;
    out.push({ kind: o.kind as FlowerKind, x: o.x, y: o.y });
  }
  return out;
}

export function plantFlower(
  garden: readonly PlantedFlower[],
  flower: PlantedFlower,
  max = 40,
): PlantedFlower[] {
  if (garden.length >= max) return [...garden];
  // unikaj nakładania w tym samym miejscu (±24px)
  const clash = garden.some(
    (f) => Math.hypot(f.x - flower.x, f.y - flower.y) < 24,
  );
  if (clash) return [...garden];
  return [...garden, { ...flower }];
}

export function flowerEmoji(kind: FlowerKind): string {
  return FLOWER_CATALOG.find((f) => f.kind === kind)?.emoji ?? '🌸';
}

export function priceLabelPl(price = GARDEN_PRICE_ZL): string {
  return `${price} zł`;
}

export function unlockHintPl(): string {
  return 'Głaszcz pieski, graj w gierki i zbieraj złote monety!';
}
